import { TIGER_TO_UNIX_OFFSET_SECS } from "./tigerTime";
import { PLOT_COLOURS } from "./plotColours";

export type TagGraphMode = "graph" | "trend";

/** A backend time window in Tiger seconds (since 1901-01-01), as strings ready
 *  to drop into `start_time=`/`end_time=` params. */
export interface TigerTimeWindow {
  startTime: string;
  endTime: string;
}

/**
 * Compute the [now − N days, now] window for a historical-duration trend,
 * matching the C++ `create_trend_dialog(N)` which uses `end = turbine current
 * time` and `start = end − N days`. We approximate "turbine current time" with
 * wall-clock now (correct for an online session). Returned in Tiger seconds.
 *
 * `nowMs` is injectable so the arithmetic is deterministic under test.
 */
export function trendDaysWindow(days: number, nowMs: number = Date.now()): TigerTimeWindow {
  const endUnix = Math.floor(nowMs / 1000);
  const startUnix = endUnix - days * 86400;
  return {
    startTime: String(startUnix + TIGER_TO_UNIX_OFFSET_SECS),
    endTime: String(endUnix + TIGER_TO_UNIX_OFFSET_SECS),
  };
}

/**
 * Build a graph-window prepop params string.
 *   "graph" mode → 1 plot per tag, graph_type=2.
 *   "trend" mode → suffixes.length plots per tag, graph_type=1, grouped by tag
 *                  (e.g. for two tags A,B and suffixes [AVG,MAX,MIN] →
 *                  A(AVG), A(MAX), A(MIN), B(AVG), B(MAX), B(MIN)).
 *
 * Each plot is emitted with both `plot[i].tag_name` and `plot[i].colour`,
 * the latter cycled from `PLOT_COLOURS` so plots stay distinguishable. The
 * graph window honours these colours verbatim and does not auto-pick a stroke
 * on the live/trend prepop path, so the colour is required to keep the line
 * visible against the chart background.
 *
 * graph_type: 1 = TrendGraphMsg (live ONLINE), 2 = DataGraphMsg (live ONLINE).
 * Mapping matches GraphWindow.tsx:90.
 *
 * Caller must pass a non-empty trendSuffixes array when mode === "trend";
 * an empty array produces zero plots. The right-click menu wiring gates this
 * via getTrendSuffixes(tiger_func).
 *
 * When `window` is supplied (only meaningful for "trend" mode, the
 * 30/180/365-day items), the request becomes a historical fetch:
 * `session_type=HIST` with top-level `start_time`/`end_time` in Tiger seconds.
 * Without it the request stays live (`session_type=ONLINE`).
 */
export function buildTagGraphParams(
  tags: string[],
  mode: TagGraphMode,
  trendSuffixes: readonly string[] = [],
  window?: TigerTimeWindow,
): string {
  const plots = mode === "trend"
    ? tags.flatMap(t => trendSuffixes.map(s => `${t}${s}`))
    : tags;

  const graphType = mode === "trend" ? 1 : 2;
  const parts = [
    `session_type=${window ? "HIST" : "ONLINE"}`,
    `graph_type=${graphType}`,
    `number_of_plots=${plots.length}`,
    ...plots.flatMap((tag, i) => [
      `plot[${i}].tag_name=${tag}`,
      `plot[${i}].colour=${PLOT_COLOURS[i % PLOT_COLOURS.length]}`,
    ]),
  ];
  if (window) {
    parts.push(`start_time=${window.startTime}`, `end_time=${window.endTime}`);
  }
  return parts.join("|");
}
